import { isEmpty } from 'lodash';
import { CustomerSummaryItem } from './types';

/** Placeholder shown when a ticket has no customers (e.g. General Feedback). */
export const NO_CUSTOMER_PLACEHOLDER = '—';

/**
 * Extracts the first non-empty account name from a customer, trimmed.
 * Returns an empty string when the name is missing/blank.
 */
const firstName = (customer: CustomerSummaryItem | undefined): string =>
    (customer?.accountName ?? '').trim();

/**
 * Formats a multi-customer list into the `[first name] +N more` summary used on
 * the seller list cards, admin queue, detail header, and parent-child tab.
 *
 * - 0 customers (or all blank) → {@link NO_CUSTOMER_PLACEHOLDER}.
 * - 1 customer                 → just the name (no suffix), matching legacy tickets.
 * - N customers                → `Ada +2 more` (suffix counts the *remaining* customers).
 *
 * Entries without a usable name are ignored for both the label and the count so
 * a stray blank never renders as `+1 more`.
 *
 * `formatName` is applied to the first (displayed) name only, so each surface can
 * keep its existing single-customer name rendering (e.g. the admin surfaces run
 * the name through `formatCustomerNames`) while the multi-customer suffix stays
 * consistent. It defaults to identity.
 */
export const formatCustomerSummary = (
    customers: CustomerSummaryItem[] | undefined,
    formatName: (name: string) => string = (name) => name,
): string => {
    if (isEmpty(customers)) {
        return NO_CUSTOMER_PLACEHOLDER;
    }

    const names = (customers as CustomerSummaryItem[])
        .map(firstName)
        .filter((name) => !isEmpty(name));

    if (names.length === 0) {
        return NO_CUSTOMER_PLACEHOLDER;
    }

    if (names.length === 1) {
        return formatName(names[0]);
    }

    const remaining = names.length - 1;
    return `${formatName(names[0])} +${remaining} more`;
};

/** True when a ticket renders as multi-customer (more than one named customer). */
export const isMultiCustomer = (customers: CustomerSummaryItem[] | undefined): boolean => {
    if (isEmpty(customers)) {
        return false;
    }
    const namedCount = (customers as CustomerSummaryItem[]).filter(
        (customer) => !isEmpty(firstName(customer)),
    ).length;
    return namedCount > 1;
};
